import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export function ErrorState({
  title = "We couldn't load this page",
  description = "Check your connection and try again. If it keeps happening, sign out and back in.",
  onRetry,
  actionTo,
  actionLabel,
  icon: Icon = AlertTriangle,
  testid = "error-state",
}) {
  return (
    <div
      className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-destructive/20 bg-destructive/5 p-10 text-center"
      data-testid={testid}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
        <Icon className="h-6 w-6" />
      </div>
      <p className="mt-4 font-display text-lg font-bold">{title}</p>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        {onRetry && (
          <Button onClick={onRetry} className="gap-2" data-testid={`${testid}-retry`}>
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        )}
        {actionTo && (
          <Button asChild variant="outline">
            <Link to={actionTo}>{actionLabel}</Link>
          </Button>
        )}
      </div>
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  actionTo,
  actionLabel,
  onAction,
  testid = "empty-state",
}) {
  return (
    <div
      className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 bg-card/40 p-10 text-center"
      data-testid={testid}
    >
      {Icon && (
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-6 w-6" />
        </div>
      )}
      <p className="mt-4 font-display text-lg font-bold">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {(actionTo || onAction) && (
        <div className="mt-5">
          {actionTo ? (
            <Button asChild className="font-semibold" data-testid={`${testid}-cta`}>
              <Link to={actionTo}>{actionLabel}</Link>
            </Button>
          ) : (
            <Button onClick={onAction} className="font-semibold" data-testid={`${testid}-cta`}>
              {actionLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function GuideBanner({ children, tone = "info", testid = "guide-banner" }) {
  const tones = {
    info: "border-primary/25 bg-primary/5 text-foreground",
    warn: "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200",
    success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200",
  };
  return (
    <div className={`mb-6 rounded-xl border px-4 py-3 text-sm leading-relaxed ${tones[tone] || tones.info}`} data-testid={testid}>
      {children}
    </div>
  );
}
